import React, { useState, useMemo } from 'react';
import { useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import CollaboratorSelector, { Collaborator } from './CollaboratorSelector';
// Team connector — commented out until team-connect feature is ready for release
// import TeamConnectorSection, { ConnectedTeamsList } from './TeamConnectorSection';
import Button from './TailwindComponents/Button';
import Badge from './TailwindComponents/Badge';
import Dropdown from './TailwindComponents/Dropdown';
import ProfileImage from '../Header/ProfileImage';
// import { fetchConnectedTeams } from '../../store/actions/collaboratorActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { hideSidePanel } from '../../store/actions/sidePanelActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { formatDateTime } from '../../utils/functions';
import { PiTrashLight, PiUserLight, PiUsersThreeLight, PiStarLight, PiStarFill } from 'react-icons/pi';

interface ResourceCollaboratorsSidePanelProps {
  resourceUuid: string;
  resourceType: string;
  collaborators: any[];
  currentUserUuid?: string;
  onUpdate?: () => void;
  canAdmin?: boolean;
  addCollaborator: (resourceUuid: string, userUuid: string, access: string) => Promise<any>;
  removeCollaborator: (resourceUuid: string, collaboratorUuid: string) => Promise<any>;
  updateAccess: (resourceUuid: string, collaboratorUuid: string, access: string) => Promise<any>;
  // Header customization
  title?: string;
  subMessage?: string;
  icon?: React.ReactNode;
  // Lead extension (clients use this; other resources use creator/admin role detection)
  supportsLead?: boolean;
  isLead?: (collab: any) => boolean;
  setLead?: (resourceUuid: string, collaboratorUuid: string) => Promise<any>;
  ownerLabel?: string; // e.g. "Lead" for clients, "Owner" otherwise
}

const ACCESS_OPTIONS = [
  { value: 'view', label: 'View' },
  { value: 'edit', label: 'Edit' },
];

// Includes 'admin' for backward-compatible read-only rendering of legacy rows.
const ACCESS_LABELS: Record<string, string> = {
  view: 'View',
  edit: 'Edit',
  admin: 'Admin',
};

const SOURCE_LABELS: Record<string, string> = {
  team: 'via team',
  department: 'via department',
  org: 'via org',
};

const ResourceCollaboratorsSidePanel: React.FC<ResourceCollaboratorsSidePanelProps> = ({
  resourceUuid,
  resourceType,
  collaborators: initialCollaborators,
  currentUserUuid,
  onUpdate,
  canAdmin = true,
  addCollaborator,
  removeCollaborator,
  updateAccess,
  title,
  subMessage,
  icon,
  supportsLead = false,
  isLead,
  setLead,
  ownerLabel,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [pendingLeadUuid, setPendingLeadUuid] = useState<string | null>(null);
  // Team connector state — commented out until team-connect feature is ready for release
  const connectedTeams: Array<{ team_uuid: string; team_name: string; member_count: number }> = [];
  // const [connectedTeams, setConnectedTeams] = useState<
  //   Array<{ team_uuid: string; team_name: string; member_count: number }>
  // >([]);
  // const loadConnectedTeams = useCallback(async () => {
  //   const teams = await (dispatch as any)(
  //     fetchConnectedTeams(resourceType, resourceUuid)
  //   );
  //   setConnectedTeams(teams || []);
  // }, [dispatch, resourceType, resourceUuid]);
  // useEffect(() => {
  //   loadConnectedTeams();
  // }, [loadConnectedTeams]);

  // Identify the owner/creator (or lead, when supportsLead).
  const ownerCollaborator = useMemo(
    () =>
      supportsLead && isLead
        ? initialCollaborators.find((c: any) => isLead(c))
        : initialCollaborators.find((c: any) => c.role === 'creator' || c.access === 'admin'),
    [initialCollaborators, supportsLead, isLead]
  );

  const creatorUuid = useMemo(() => {
    return ownerCollaborator?.user_id ? String(ownerCollaborator.user_id) : currentUserUuid || '';
  }, [ownerCollaborator, currentUserUuid]);

  // Predicate: true if a row should be excluded from the editable list (i.e. owner/lead).
  const isOwnerRow = useMemo(
    () => (c: any) =>
      supportsLead && isLead
        ? isLead(c)
        : c.role === 'creator' || c.access === 'admin',
    [supportsLead, isLead]
  );

  // Separate collaborators by source
  const { teamCollaborators, directCollaborators } = useMemo(() => {
    const nonOwner = initialCollaborators.filter((c: any) => !isOwnerRow(c));
    const teamCollabs: any[] = [];
    const directCollabs: any[] = [];
    for (const c of nonOwner) {
      if (c.source === 'team') {
        teamCollabs.push(c);
      } else {
        directCollabs.push(c);
      }
    }
    return { teamCollaborators: teamCollabs, directCollaborators: directCollabs };
  }, [initialCollaborators, isOwnerRow]);

  // All non-owner collaborators mapped to Collaborator shape
  const mappedInitial: Collaborator[] = useMemo(
    () =>
      initialCollaborators
        .filter((c: any) => !isOwnerRow(c))
        .map((c: any) => ({
          user_id: c.user_id,
          collaborator_uuid: c.collaborator_uuid || String(c.user_id),
          name: c.name || '',
          email: c.email || '',
          initials: c.initials || '',
          profile_picture: c.profile_picture || null,
          access: c.access || 'view',
          source: (c.source as Collaborator['source']) || 'direct',
          granted_via_uuid: c.granted_via_uuid || undefined,
          shared_by_user: c.shared_by_user || undefined,
          shared_at: c.shared_at || undefined,
        })),
    [initialCollaborators, isOwnerRow]
  );

  const [pendingCollaborators, setPendingCollaborators] = useState<Collaborator[]>(mappedInitial);

  const handleAccessChange = (collab: Collaborator, newAccess: Collaborator['access']) => {
    setPendingCollaborators((prev) =>
      prev.map((c) => (c.user_id === collab.user_id ? { ...c, access: newAccess } : c))
    );
  };

  const handleRemoveCollaborator = (collab: Collaborator) => {
    dispatch(
      showModal({
        showModal: true,
        type: 'confirmation',
        message: t('Remove Access'),
        subMsg: t(
          'Remove {{name}} from this resource? Any access granted through this resource will be automatically revoked.',
          { name: collab.name || collab.email || 'this user' }
        ),
        isDest: true,
        onConfirm: () => {
          setPendingCollaborators((prev) =>
            prev.filter((c) => c.user_id !== collab.user_id)
          );
          dispatch(hideModal());
        },
      })
    );
  };

  const handleSetLead = (collaboratorUuid: string) => {
    setPendingLeadUuid((prev) => (prev === collaboratorUuid ? null : collaboratorUuid));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const originalIds = new Set(mappedInitial.map((c) => c.user_id));
      const currentIds = new Set(pendingCollaborators.map((c) => c.user_id));

      let addedCount = 0;
      let removedCount = 0;
      let accessChangedCount = 0;
      let leadChanged = false;

      for (const collab of pendingCollaborators) {
        if (!originalIds.has(collab.user_id)) {
          await addCollaborator(
            resourceUuid,
            (collab.collaborator_uuid || String(collab.user_id)) as string,
            collab.access || 'view'
          );
          addedCount++;
        }
      }

      for (const collab of mappedInitial) {
        if (!currentIds.has(collab.user_id)) {
          await removeCollaborator(resourceUuid, collab.collaborator_uuid || '');
          removedCount++;
        }
      }

      for (const collab of pendingCollaborators) {
        const original = mappedInitial.find((m) => m.user_id === collab.user_id);
        if (original && original.access !== collab.access) {
          await updateAccess(
            resourceUuid,
            collab.collaborator_uuid || '',
            collab.access || 'view'
          );
          accessChangedCount++;
        }
      }

      if (supportsLead && setLead && pendingLeadUuid) {
        await setLead(resourceUuid, pendingLeadUuid);
        leadChanged = true;
      }

      const totalChanges = addedCount + removedCount + accessChangedCount + (leadChanged ? 1 : 0);
      if (totalChanges > 0) {
        // dispatch(
        //   showFeedbackModal({
        //     showModal: true,
        //     modalType: 'success' as any,
        //     message: t('{{count}} change(s) saved', { count: totalChanges }),
        //     duration: 3000,
        //   })
        // );
      }

      onUpdate?.();
      dispatch(hideSidePanel());
    } catch {
      dispatch(
        showFeedbackModal({
          showModal: true,
          modalType: 'error',
          message: t('Failed to update access'),
          duration: 3000,
        })
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Group team collaborators by team name for display
  const teamGroups = useMemo(() => {
    const groups: Record<string, Collaborator[]> = {};
    for (const collab of pendingCollaborators) {
      if (collab.source === 'team' && collab.granted_via_uuid) {
        const teamName =
          connectedTeams.find((ct) => ct.team_uuid === collab.granted_via_uuid)?.team_name ||
          t('Team');
        if (!groups[teamName]) groups[teamName] = [];
        groups[teamName].push(collab);
      }
    }
    return groups;
  }, [pendingCollaborators, connectedTeams, t]);

  const individualCollaborators = useMemo(
    () => pendingCollaborators.filter((c) => c.source !== 'team' || !c.granted_via_uuid),
    [pendingCollaborators]
  );

  return (
    <div className="flex flex-col h-full">
      {/* Optional header — title / subMessage / icon */}
      {(title || subMessage || icon) && (
        <div className="px-5 pt-5 pb-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            {icon && (
              <div className="flex-shrink-0 mt-0.5 text-gray-500 dark:text-gray-400">
                {icon}
              </div>
            )}
            <div className="min-w-0">
              {title && (
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {title}
                </p>
              )}
              {subMessage && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {subMessage}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add new collaborators — admin only */}
      {canAdmin && (
        <div className="px-4 pt-5 pb-4 border-b border-gray-200 dark:border-gray-700">
          <CollaboratorSelector
            collaborators={pendingCollaborators}
            onCollaboratorsChange={setPendingCollaborators}
            allowedPermissions={['view', 'edit']}
            adminUuid={creatorUuid}
            showOwnerSection={false}
            hideSelectedList
          />
        </div>
      )}

      {/* Team connector section — commented out until team-connect feature is ready for release
      <TeamConnectorSection
        resourceUuid={resourceUuid}
        resourceType={resourceType}
        connectedTeams={connectedTeams}
        onTeamConnected={() => {
          loadConnectedTeams();
          onUpdate?.();
        }}
        onTeamDisconnected={() => {
          loadConnectedTeams();
          onUpdate?.();
        }}
        canAdmin={canAdmin}
        existingCollaboratorUuids={[
          ...pendingCollaborators.map(c => c.collaborator_uuid || String(c.user_id || '')),
          ...(ownerCollaborator ? [ownerCollaborator.collaborator_uuid || String(ownerCollaborator.user_id || '')] : []),
          ...(currentUserUuid ? [currentUserUuid] : []),
        ].filter(Boolean)}
      /> */}

      {/* Unified collaborators list */}
      <div className="flex-1 overflow-y-auto custom-scrollbar-main px-5 py-3">
        {!ownerCollaborator && pendingCollaborators.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-400 dark:text-gray-500">
            <PiUserLight className="w-8 h-8 mb-2 opacity-40" />
            <p className="text-sm">{t('No one has access yet')}</p>
          </div>
        ) : (
          <div className="space-y-0.5">
            {/* Owner row — prominently highlighted */}
            {ownerCollaborator && (
              <div className="flex items-center gap-3 py-3 px-3 rounded-lg bg-primary-50/50 dark:bg-primary-900/10 border border-primary-100 dark:border-primary-900/20 mb-2">
                <ProfileImage
                  profilePicture={ownerCollaborator.profile_picture || null}
                  email={ownerCollaborator.email || ''}
                  initials={ownerCollaborator.initials || '??'}
                  name={ownerCollaborator.name || ''}
                  size="w-9 h-9"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {ownerCollaborator.name || ownerCollaborator.email}
                    </p>
                    <Badge color={supportsLead ? 'blue' : 'indigo'}>
                      {t(ownerLabel || (supportsLead ? 'Lead' : 'Owner'))}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">
                    {ownerCollaborator.email}
                  </p>
                </div>
                {supportsLead && (
                  <PiStarFill className="w-4 h-4 text-amber-500 flex-shrink-0" />
                )}
              </div>
            )}

            {/* Team-grouped collaborators */}
            {Object.entries(teamGroups).map(([teamName, members]) => (
              <div key={teamName} className="mt-3">
                <div className="flex items-center gap-2 px-2 mb-1">
                  <PiUsersThreeLight className="w-3.5 h-3.5 text-gray-400" />
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {teamName}
                  </p>
                </div>
                {members.map((collab) => (
                  <CollaboratorRow
                    key={collab.user_id}
                    collab={collab}
                    initialCollaborators={initialCollaborators}
                    mappedInitial={mappedInitial}
                    canAdmin={canAdmin}
                    onAccessChange={handleAccessChange}
                    onRemove={handleRemoveCollaborator}
                    supportsLead={supportsLead}
                    isPendingLead={
                      !!pendingLeadUuid &&
                      pendingLeadUuid === String(collab.collaborator_uuid || collab.user_id)
                    }
                    onSetLead={handleSetLead}
                    t={t}
                  />
                ))}
              </div>
            ))}

            {/* Individual (direct) collaborators */}
            {individualCollaborators.length > 0 && Object.keys(teamGroups).length > 0 && (
              <div className="mt-3 px-2 mb-1">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  {t('Individual Access')}
                </p>
              </div>
            )}
            {individualCollaborators.map((collab) => (
              <CollaboratorRow
                key={collab.user_id}
                collab={collab}
                initialCollaborators={initialCollaborators}
                mappedInitial={mappedInitial}
                canAdmin={canAdmin}
                onAccessChange={handleAccessChange}
                onRemove={handleRemoveCollaborator}
                t={t}
              />
            ))}
          </div>
        )}
      </div>

      {/* Connected teams — commented out until team-connect feature is ready for release
      <ConnectedTeamsList
        resourceUuid={resourceUuid}
        resourceType={resourceType}
        connectedTeams={connectedTeams}
        onTeamDisconnected={() => { loadConnectedTeams(); onUpdate?.(); }}
        canAdmin={canAdmin}
      /> */}

      {/* Footer */}
      <div className="border-t border-gray-200 dark:border-gray-700 px-5 py-4 flex justify-end gap-2">
        <Button variant="neutral" size="small" onClick={() => dispatch(hideSidePanel())}>
          {canAdmin ? t('Cancel') : t('Close')}
        </Button>
        {canAdmin && (
          <Button variant="primary" size="small" onClick={handleSave} disabled={isSaving}>
            {isSaving ? t('Saving...') : t('Save')}
          </Button>
        )}
      </div>
    </div>
  );
};

/* ─── Extracted row component for reuse ─── */

interface CollaboratorRowProps {
  collab: Collaborator;
  initialCollaborators: any[];
  mappedInitial: Collaborator[];
  canAdmin: boolean;
  onAccessChange: (collab: Collaborator, access: Collaborator['access']) => void;
  onRemove: (collab: Collaborator) => void;
  supportsLead?: boolean;
  isPendingLead?: boolean;
  onSetLead?: (collaboratorUuid: string) => void;
  t: ReturnType<typeof useTranslation>['t'];
}

const CollaboratorRow: React.FC<CollaboratorRowProps> = ({
  collab,
  initialCollaborators,
  mappedInitial,
  canAdmin,
  onAccessChange,
  onRemove,
  supportsLead = false,
  isPendingLead = false,
  onSetLead,
  t,
}) => {
  const original = initialCollaborators.find((c: any) => c.user_id === collab.user_id);
  const sharedByUser = original?.shared_by_user;
  const sharedAt = original?.shared_at;
  const source = original?.source || collab.source || 'direct';
  const isNew = !mappedInitial.some((m) => m.user_id === collab.user_id);
  // Legacy non-owner rows with access === 'admin' render read-only — Admin is
  // no longer assignable but existing data must not silently downgrade.
  const isLegacyAdmin = collab.access === 'admin';

  return (
    <div className="flex items-start gap-3 py-2.5 px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
      <ProfileImage
        profilePicture={collab.profile_picture || null}
        email={collab.email || ''}
        initials={collab.initials || '??'}
        name={collab.name || ''}
        size="w-8 h-8"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {collab.name || collab.email}
          </p>
          {source !== 'direct' && SOURCE_LABELS[source] && (
            <Badge color="gray">{t(SOURCE_LABELS[source])}</Badge>
          )}
          {isNew && <Badge color="green">{t('New')}</Badge>}
          {isPendingLead && <Badge color="blue">{t('Lead (pending)')}</Badge>}
        </div>
        <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">
          {collab.email}
        </p>
        {(sharedByUser || sharedAt) && (
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
            {sharedByUser?.full_name || sharedByUser?.name
              ? t('Added by {{name}}', { name: sharedByUser.full_name || sharedByUser.name })
              : t('Added')}
            {sharedAt ? ` · ${formatDateTime(sharedAt)}` : ''}
          </p>
        )}
      </div>

      {/* Access level */}
      <div className="flex-shrink-0 flex items-center gap-1">
        {canAdmin && !isLegacyAdmin ? (
          <>
            <Dropdown
              options={ACCESS_OPTIONS}
              value={collab.access || 'view'}
              onChange={(val) =>
                onAccessChange(
                  collab,
                  (Array.isArray(val) ? val[0] : val) as Collaborator['access']
                )
              }
              compact
            />
            {supportsLead && onSetLead && !isNew && (
              <button
                onClick={() =>
                  onSetLead(String(collab.collaborator_uuid || collab.user_id))
                }
                className="p-1 rounded text-gray-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                title={t('Set as lead')}
              >
                {isPendingLead ? (
                  <PiStarFill className="w-4 h-4 text-amber-500" />
                ) : (
                  <PiStarLight className="w-4 h-4" />
                )}
              </button>
            )}
            <button
              onClick={() => onRemove(collab)}
              className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              title={t('Remove')}
            >
              <PiTrashLight className="w-4 h-4" />
            </button>
          </>
        ) : (
          <Badge
            color={
              collab.access === 'edit'
                ? 'blue'
                : collab.access === 'admin'
                ? 'slate'
                : 'gray'
            }
          >
            {t(ACCESS_LABELS[collab.access] || collab.access)}
          </Badge>
        )}
      </div>
    </div>
  );
};

export default ResourceCollaboratorsSidePanel;
